import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Cart({ user }) {
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      const response = await fetch(`${BACKEND_URL}/api/cart/${sessionId}`);
      const data = await response.json();
      setCartItems(data.items || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, change) => {
    const item = cartItems.find(i => i.product_id === productId);
    if (!item) return;
    const newQuantity = item.quantity + change;
    if (newQuantity < 1) return;
    const updatedItems = cartItems.map(i =>
      i.product_id === productId ? {...i, quantity: newQuantity} : i
    );
    setCartItems(updatedItems);
    const sessionId = localStorage.getItem('sessionId');
    try {
      await fetch(`${BACKEND_URL}/api/cart/${sessionId}/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQuantity })
      });
    } catch (error) {
      toast.error(t('cart.update_error'));
      fetchCart();
    }
  };

  const removeItem = async (productId) => {
    const sessionId = localStorage.getItem('sessionId');
    try {
      await fetch(`${BACKEND_URL}/api/cart/${sessionId}/${productId}`, { method: 'DELETE' });
      toast.success(t('cart.product_removed'));
      fetchCart();
    } catch (error) {
      toast.error(t('cart.remove_error'));
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('cart.loading')}</div>;
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="text-center">
          <ShoppingCart className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{t('cart.empty')}</h2>
          <p className="text-gray-600 mb-6">{t('cart.empty_desc')}</p>
          <Link to="/productos">
            <Button className="bg-black text-white">{t('cart.go_shopping')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8e6e0' }}>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/productos" className="text-sm mb-6 inline-block hover:underline" data-testid="continue-shopping-link">← {t('cart.continue_shopping')}</Link>

        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Playfair Display' }} data-testid="cart-title">{t('cart.title')}</h1>

        <div className="space-y-4 mb-8">
          {cartItems.map((item) => (
            <div key={item.product_id} className="bg-white p-4 sm:p-6 border-2 border-black" data-testid={`cart-item-${item.product_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <Link to={`/producto/${item.product_id}`} className="flex-1 min-w-0 hover:opacity-70 transition-opacity cursor-pointer">
                  <h3 className="font-bold text-base sm:text-lg mb-1 underline-offset-2 hover:underline break-anywhere">{item.product_name}</h3>
                  {item.variant && (
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">
                      {Object.entries(item.variant).filter(([k,v]) => v).map(([k,v]) => `${k}: ${v}`).join(', ')}
                    </p>
                  )}
                  <p className="text-base sm:text-lg font-bold">{item.price.toFixed(2)}€</p>
                </Link>
                <div className="flex items-center justify-between sm:justify-normal sm:gap-6">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateQuantity(item.product_id, -1)}
                      disabled={item.quantity <= 1}
                      className="w-11 h-11 sm:w-10 sm:h-10 bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      data-testid={`qty-minus-${item.product_id}`}
                      aria-label="Reducir cantidad"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-base sm:text-lg font-bold w-10 sm:w-12 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, +1)}
                      className="w-11 h-11 sm:w-10 sm:h-10 bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                      data-testid={`qty-plus-${item.product_id}`}
                      aria-label="Aumentar cantidad"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-right sm:min-w-[120px]">
                    <p className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{(item.price * item.quantity).toFixed(2)}€</p>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="text-red-600 hover:text-red-800 text-xs sm:text-sm flex items-center gap-1 ml-auto"
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" /> {t('cart.remove')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white p-6 border-2 border-black">
          <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded mb-4">
            <p className="text-sm font-bold text-yellow-900 mb-1">{t('cart.save_more')}</p>
            <p className="text-xs text-yellow-800 mb-3">{t('cart.save_more_desc')}</p>
            <div className="bg-white border-2 border-dashed border-black px-4 py-2 text-center rounded mb-3">
              <span className="text-xs text-gray-500 block">{t('checkout.coupon_code')}</span>
              <span className="text-lg font-bold tracking-widest" data-testid="promo-code">{t('cart.save_more_code')}</span>
            </div>
            {!user && (
              <div className="flex gap-2">
                <Button onClick={() => navigate('/?action=login')} className="flex-1 bg-black text-white text-sm py-2">{t('cart.login')}</Button>
                <Button onClick={() => navigate('/?action=register')} className="flex-1 border-2 border-black bg-white text-black text-sm py-2">{t('cart.register')}</Button>
              </div>
            )}
          </div>
          <div className="flex justify-between text-lg mb-4">
            <span>{t('cart.subtotal')}:</span>
            <span className="font-bold">{subtotal.toFixed(2)}€</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">{t('cart.shipping_note')}</p>
          <Button onClick={() => navigate('/checkout')} className="w-full bg-black text-white py-4 text-lg" data-testid="proceed-checkout-btn">{t('cart.proceed')}</Button>
        </div>
      </div>
    </div>
  );
}
